import React, {useEffect, useState, useRef} from "react";
import GenericLayout from "layouts/generic";
import css from "./index.module.scss";
import ContactSection from "sections/contact";
import { useInView } from "react-intersection-observer";
import { gsap } from "scripts/gsap";
import SplitText from "@/components/splitText";
import slideFadeIn from "scripts/animations/slideFadeIn";
import inViewSettings from "scripts/animations/inViewSettings";
import TacticsSmall, {TACTICS} from "@/sections/tacticsSmall";
import TestimonialsSpotlight, {TESTIMONIALS} from "@/sections/testimonialsSpotlight";
import Fold1Section from "@/sections/fold1";
import BasicCardSection from "@/sections/basicCards";
import GraphicSvg from "./graphic.svg";
import Link from "next/link";

export default function SeoAdvisory({}) {
  const [elements, setElements] = useState({
    fold: {
      infographic: useInView(inViewSettings((targets) => {
        let graphic = targets[0];
        let tl = gsap.timeline({});

        let bars = graphic.querySelectorAll(".bar");

        let texts = graphic.querySelectorAll(".text");

        tl.set(graphic, {visibility: "visible", opacity: 1});
        tl.fromTo(bars, {
          opacity: 0,
          translateY: "-25px",
          transformOrigin: "center top",
        }, {
          opacity: 1,
          scaleY: 1,
          translateY: "0%",
          duration: 0.25,
          ease: "sine.inOut",
          stagger: {
            each: 0.15,
          },
        });
      })),
    },
    explanation: {
      text: useInView(inViewSettings(slideFadeIn)),
      headline: useInView(inViewSettings(slideFadeIn)),
    },
    explanation2: {
      text: useInView(inViewSettings(slideFadeIn)),
      headline: useInView(inViewSettings(slideFadeIn)),
    },
  });

  return <GenericLayout
    title="Boost Your Website's Visibility with On-Site SEO Advisory | Deviate Labs"
    description="Our expert ad team develops breakthrough creatives tailored to your brand's unique identity and goals."
    color="purple"
  >
    <Fold1Section
      headline={<>
        <div>Boost Your Website</div>
        <div>Visibility with</div>
        <div>On-site SEO Advisory</div>
      </>}
      className={css.fold}
      subheadline="Why pay for traffic  when you can own your organic impressions?"
      image={
        <div className={`target ${css.graphic}`} ref={elements.fold.infographic.ref}>
          <GraphicSvg/>
        </div>
      }
    />


    <section className={`section ${css.explanation}`}>
      <div className="sectionInner">
        <h2 ref={elements.explanation.headline.ref}>
          <SplitText className="target" text="SEO Consulting Services & Expert Consultants"/>
        </h2>

        <div className={css.text} ref={elements.explanation.text.ref}>
          <p>
            <SplitText className="target" text="Deviate Labs offers a comprehensive, full-service approach to Search Engine Optimization (SEO), while a solo consultant is limited in their capabilities. With an entire team of experts under one roof, we can audit technical issues, create content strategies, monitor rankings, and execute link-building campaigns more effectively than any single person could."/>
          </p>
          <p>
            <SplitText className="target" text="Our multifaceted SEO strategies are crafted by specialists in several key areas, including technical audits, content creation, link acquisition, and more. This diversity of expertise allows us to develop holistic campaigns tailored to each client's unique goals and needs. A lone consultant simply can't match the depth and breadth of skills our agency offers."/>
          </p>
          <p>
            <SplitText className="target" text="At Deviate Labs, we don't just tell you what to do — we partner with you every step of the way during strategy development and execution. Our hands-on guidance ensures your team fully understands our recommendations so you see sustainable success long after our engagement ends. Solo consultants lack the bandwidth to provide this level of service."/>
          </p>
        </div>
      </div>
    </section>



    <BasicCardSection
      headline="Comprehensive SEO Audits"
      subheadline="We handle both on-page and off-page Search Engine Optimization."
      width="2"
      color="cyan"
      cards={[
        {
          name: "Detailed SEO Strategy",
          body: <>We crawl your site to uncover technical issues impacting rankings like site speed, broken links, meta tags, etc. Our audits also evaluate the quality of existing content and analyze competitors&apos; strategies. Based on audit findings and your business goals, we develop a data-driven SEO strategy tailored to your website. Our strategic plans outline target keywords, necessary website fixes, recommended content topics, and link building opportunities.</>,
        },
        {
          name: "SEO Implementation Guidance",
          body: <>We don&apos;t just hand you a strategy and walk away. Our experts will guide your team through prioritizing and implementing our recommendations to ensure you see desired results. We&apos;re invested in your SEO success from start to finish.</>,
        },
        {
          name: "Organic Keyword Monitoring",
          body: <>We track your site&apos;s keyword rankings and traffic regularly to measure SEO efforts against baselines. Our monitoring enables us to course correct as needed to keep your strategy on track for achieving your goals.</>,
        },
        {
          name: "AI Content Marketing",
          body: <>We leverage leading AI technology to efficiently create high-quality, engaging content tailored to your target keywords and audience preferences. Our AI-generated blogs, social posts, and emails save you time while fueling your organic growth.</>,
        },
      ]}
    />



    <section className={`section ${css.explanation2}`}>
      <div className="sectionInner">
        <h2 ref={elements.explanation2.headline.ref}>
          <SplitText className="target" text="SEO Expertise That Delivers Results"/>
        </h2>

        <div className={css.text} ref={elements.explanation2.text.ref}>
          <p>
            <SplitText className="target" text="Give your website the competitive edge it needs with SEO consulting services from Deviate Labs. As the experts who literally wrote the"/><Link href="/book"><a className="target">book</a></Link><SplitText className="target" text=" on growth hacking, our agency knows what it takes to achieve top rankings, increased traffic, and more conversions."/>
          </p>
          <h3 className="target">Tailored SEO Strategy for Your Business</h3>
          <p>
            <SplitText className="target" text="Each client we work with is unique. We'll discuss your brand, website history, and goals to create a bespoke SEO strategy. Our experts will help identify and advise a strategy to help your website rank highly across your most valuable key terms."/>
          </p>
          <h3 className="target">Comprehensive SEO Services That Drive Results</h3>
          <p>
            <SplitText className="target" text="We provide a vast array of SEO services including detailed audits, keyword research and monitoring, implementation guidance, competitor analysis, backlink audit, PR outreach, and content marketing advisory."/>
          </p>
        </div>
      </div>
    </section>

    <TestimonialsSpotlight testimonial={TESTIMONIALS.HR_CLOUD}/>

    <TacticsSmall
      headline="We Offer a Full Range of SEO Services"
      subheadline="Whatever your goals, we have the expertise to optimize your growth."
      enabled={[
        TACTICS.HIGH_PERFORMANCE_LANDING_PAGES,
        TACTICS.CONTENT_MARKETING,
        TACTICS.SONIC_BRANDING,
        TACTICS.INFLUENCER_MARKETING,
        TACTICS.B2B_SAAS_GROWTH_MARKETING,
      ]}
    />

    <ContactSection/>


  </GenericLayout>;
}
